/**
 * DashboardApprovals — Iter 175i.
 *
 * Lives at /website-builder/approvals. Lets the site owner review pending
 * access requests from EntrepreNatives Support (the master). They can
 * Allow (grants edit access until they remove it later) or Reject.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, ShieldCheck, ShieldOff, MessageSquareText, Clock, CheckCircle2, XCircle, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function DashboardApprovals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/builder/access-requests`, { headers: auth() });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Failed to load approvals');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/builder/access-requests/${id}/${action}`, {}, { headers: auth() });
      toast.success(action === 'approve' ? 'Access granted. They can edit your site until you remove them.' : 'Request rejected.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setBusyId(null); }
  };

  const pending = items.filter(i => i.status === 'pending');
  const history = items.filter(i => i.status !== 'pending');

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 space-y-8" data-testid="dashboard-approvals">
      <div>
        <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Permissions</p>
        <h1 className="text-3xl font-bold text-white font-syne mt-1">Approvals inbox</h1>
        <p className="text-sm text-white/65 mt-2 max-w-xl">
          When EntrepreNatives Support asks for edit access to help you finish or polish your site, the request shows up here. You can revoke access any time from Settings → Permissions &amp; Ownership.
        </p>
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="animate-spin inline text-[#A78BFA]" /></div>
      ) : (
        <>
          <section>
            <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-white/55 mb-3">Pending</h2>
            {pending.length === 0 ? (
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-8 text-center text-white/55 text-sm" data-testid="approvals-empty">
                <Inbox size={28} className="mx-auto mb-2 text-white/30" />
                Nothing pending. Welcome to inbox zero.
              </div>
            ) : (
              <div className="space-y-3">
                {pending.map(req => (
                  <div
                    key={req.id}
                    className="bg-white/[0.04] border border-amber-400/15 rounded-2xl p-5 shadow-[0_0_30px_rgba(251,191,36,0.08)]"
                    data-testid={`approval-${req.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <ShieldCheck size={16} className="text-amber-300" />
                          <p className="font-bold text-white text-sm">EntrepreNatives Support wants edit access</p>
                          <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-amber-300 bg-amber-500/10 border border-amber-400/25 px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock size={9} /> Pending
                          </span>
                        </div>
                        <p className="text-xs text-white/55 mt-1.5 font-mono">
                          Requested {new Date(req.created_at).toLocaleString()}
                        </p>
                        {req.message && (
                          <div className="mt-3 bg-white/[0.04] border-l-2 border-[#A78BFA] rounded-r-lg px-3 py-2 text-sm text-white/80 italic flex items-start gap-2">
                            <MessageSquareText size={13} className="mt-0.5 text-[#A78BFA] shrink-0" />
                            <span>{req.message}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => act(req.id, 'reject')}
                          disabled={busyId === req.id}
                          className="px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-[0.16em] text-red-300 hover:bg-red-500/10 border border-red-400/20 transition-all disabled:opacity-40"
                          data-testid={`approval-reject-${req.id}`}
                        >
                          <XCircle size={13} className="inline mr-1.5" /> Reject
                        </button>
                        <button
                          onClick={() => act(req.id, 'approve')}
                          disabled={busyId === req.id}
                          className="px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-[0.16em] bg-gradient-to-r from-emerald-500 to-emerald-600 text-white transition-all disabled:opacity-40"
                          data-testid={`approval-approve-${req.id}`}
                        >
                          <CheckCircle2 size={13} className="inline mr-1.5" /> Allow
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {history.length > 0 && (
            <section>
              <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-white/55 mb-3">History</h2>
              <div className="space-y-2">
                {history.map(req => (
                  <div key={req.id} className="bg-white/[0.02] border border-white/5 rounded-xl p-4 flex items-center justify-between text-sm" data-testid={`approval-history-${req.id}`}>
                    <div className="flex items-center gap-3">
                      {req.status === 'approved'
                        ? <ShieldCheck size={14} className="text-emerald-300" />
                        : <ShieldOff size={14} className="text-red-300" />}
                      <div>
                        <p className="text-white/85">EntrepreNatives Support · <span className={req.status === 'approved' ? 'text-emerald-300' : 'text-red-300'}>{req.status}</span></p>
                        <p className="text-[11px] text-white/40">Decided {req.decided_at ? new Date(req.decided_at).toLocaleString() : '—'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
